import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import { CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
  format,
  parseISO,
  eachDayOfInterval,
  isWeekend,
  isSunday,
} from "date-fns";

const accent = { from: "#4f46e5", to: "#7c3aed" };
const surface = { card: "#ffffff", muted: "#f8fafc", border: "#e2e8f0", borderStrong: "#cbd5e1" };

const styles = {
  container: {
    position: "relative",
    width: "100%",
    minHeight: "calc(100vh - 120px)",
    overflowX: "hidden",
    backgroundColor: "#f1f5f9",
    backgroundImage: `
      radial-gradient(ellipse 900px 480px at 0% -10%, rgba(79, 70, 229, 0.12), transparent 55%),
      radial-gradient(ellipse 800px 400px at 100% 0%, rgba(124, 58, 237, 0.10), transparent 50%),
      linear-gradient(180deg, #f8fafc 0%, #f1f5f9 35%, #eef2f7 100%)
    `,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: "clamp(12px, 2vw, 24px)",
    boxSizing: "border-box",
  },
  shell: {
    maxWidth: 1040,
    margin: "0 auto",
  },
  topBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 20,
    flexWrap: "wrap",
  },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 44,
    height: 44,
    borderRadius: 12,
    border: `1px solid ${surface.borderStrong}`,
    backgroundColor: "rgba(255,255,255,0.85)",
    backdropFilter: "blur(8px)",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
    color: "#475569",
    cursor: "pointer",
    transition: "background-color 0.2s, box-shadow 0.2s, transform 0.15s",
  },
  hero: {
    textAlign: "center",
    marginBottom: 24,
    padding: "0 8px",
  },
  badge: {
    display: "inline-block",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#4f46e5",
    backgroundColor: "rgba(79, 70, 229, 0.10)",
    border: "1px solid rgba(79, 70, 229, 0.2)",
    padding: "6px 12px",
    borderRadius: 999,
    marginBottom: 14,
  },
  pageTitle: {
    margin: 0,
    fontSize: "clamp(1.5rem, 4vw, 2rem)",
    fontWeight: 800,
    letterSpacing: "-0.03em",
    color: "#0f172a",
    lineHeight: 1.2,
  },
  pageTitleGradient: {
    backgroundImage: `linear-gradient(120deg, ${accent.from} 0%, ${accent.to} 45%, #4338ca 100%)`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
  pageSubtitle: {
    margin: "12px auto 0",
    maxWidth: 420,
    fontSize: 15,
    lineHeight: 1.55,
    color: "#64748b",
    fontWeight: 400,
  },
  downloadBtn: (isHover) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    backgroundColor: isHover ? "#ffffff" : "rgba(255,255,255,0.9)",
    color: "#4338ca",
    border: `1px solid ${isHover ? "#c7d2fe" : surface.borderStrong}`,
    padding: "10px 18px",
    borderRadius: 12,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05)",
    transition: "border-color 0.2s, background-color 0.2s, box-shadow 0.2s",
  }),
  filterCard: {
    backgroundColor: surface.card,
    borderRadius: 16,
    border: `1px solid ${surface.border}`,
    boxShadow: "0 4px 6px -1px rgba(15, 23, 42, 0.06), 0 10px 24px -8px rgba(15, 23, 42, 0.08)",
    padding: "clamp(20px, 3vw, 28px)",
    marginBottom: 28,
  },
  cardHeader: {
    marginBottom: 22,
    paddingBottom: 18,
    borderBottom: `1px solid ${surface.border}`,
  },
  cardTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: 700,
    color: "#0f172a",
    letterSpacing: "-0.02em",
  },
  cardHint: {
    margin: "6px 0 0",
    fontSize: 13,
    color: "#64748b",
    lineHeight: 1.45,
  },
  formRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 20,
    alignItems: "flex-end",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    flex: "1 1 200px",
    minWidth: 0,
  },
  formGroupEmployee: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    flex: "2 1 280px",
    minWidth: 0,
  },
  employeePickerBlock: {
    width: "100%",
    marginBottom: 22,
  },
  employeeToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 10,
  },
  employeeToolbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
  },
  employeeToolbarActions: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  textBtn: {
    background: "none",
    border: "none",
    color: "#4f46e5",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    padding: "6px 10px",
    borderRadius: 8,
  },
  textBtnMuted: {
    color: "#64748b",
  },
  selectedCount: {
    fontSize: 13,
    fontWeight: 600,
    color: "#334155",
  },
  checkboxGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: 4,
    maxHeight: 260,
    overflowY: "auto",
    padding: "12px 14px",
    border: `1px solid ${surface.borderStrong}`,
    borderRadius: 10,
    backgroundColor: "#fff",
    boxSizing: "border-box",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
    color: "#0f172a",
    border: `1px solid transparent`,
    transition: "background-color 0.15s, border-color 0.15s",
  },
  checkboxLabelDisabled: {
    opacity: 0.55,
    cursor: "not-allowed",
  },
  checkboxInput: {
    width: 18,
    height: 18,
    accentColor: accent.from,
    cursor: "pointer",
    flexShrink: 0,
  },
  formActions: {
    display: "flex",
    flex: "1 1 200px",
    minWidth: 200,
    justifyContent: "stretch",
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.02em",
    color: "#475569",
    textTransform: "uppercase",
  },
  input: {
    padding: "12px 14px",
    border: `1px solid ${surface.borderStrong}`,
    borderRadius: 10,
    fontSize: 14,
    outline: "none",
    backgroundColor: "#fff",
    color: "#0f172a",
    transition: "border-color 0.2s, box-shadow 0.2s",
    boxSizing: "border-box",
    width: "100%",
  },
  inputFocus: {
    borderColor: "#818cf8",
    boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.15)",
  },
  submitBtn: (loading) => ({
    width: "100%",
    minHeight: 46,
    background: loading
      ? "#94a3b8"
      : `linear-gradient(135deg, ${accent.from} 0%, ${accent.to} 100%)`,
    color: "#fff",
    padding: "12px 22px",
    border: "none",
    borderRadius: 10,
    cursor: loading ? "not-allowed" : "pointer",
    fontSize: 15,
    fontWeight: 700,
    letterSpacing: "0.01em",
    boxShadow: loading ? "none" : "0 4px 14px rgba(79, 70, 229, 0.35)",
    transition: "transform 0.15s, box-shadow 0.2s, opacity 0.2s",
  }),
  errorMessage: {
    color: "#b91c1c",
    marginTop: 16,
    fontSize: 14,
    padding: "12px 14px",
    backgroundColor: "#fef2f2",
    borderRadius: 10,
    border: "1px solid #fecaca",
    lineHeight: 1.45,
  },
  employeeSection: {
    marginBottom: 28,
  },
  resultCard: {
    backgroundColor: surface.card,
    borderRadius: 16,
    border: `1px solid ${surface.border}`,
    boxShadow: "0 2px 4px rgba(15, 23, 42, 0.04)",
    overflow: "hidden",
    marginBottom: 24,
  },
  employeeTitleBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
    padding: "18px 22px",
    background: "linear-gradient(90deg, #1e1b4b 0%, #312e81 50%, #3730a3 100%)",
    color: "#fff",
  },
  employeeTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: "-0.01em",
  },
  employeeTitleMeta: {
    fontSize: 12,
    fontWeight: 500,
    opacity: 0.85,
  },
  tableContainer: {
    overflow: "hidden",
    margin: 0,
  },
  headerRow: {
    display: "flex",
    background: "linear-gradient(180deg, #334155 0%, #1e293b 100%)",
    color: "#fff",
    fontWeight: 600,
    fontSize: 12,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  headerCell: (width) => ({
    padding: "14px 16px",
    borderRight: "1px solid rgba(255,255,255,0.12)",
    ...(width ? { width } : { flex: 1 }),
  }),
  dataRow: (isWeekend, isEven) => ({
    display: "flex",
    borderBottom: `1px solid ${surface.border}`,
    backgroundColor: isWeekend ? "#f1f5f9" : isEven ? "#fafbfc" : "#fff",
    fontSize: 14,
  }),
  dataCell: (width, color) => ({
    padding: "12px 16px",
    borderRight: `1px solid ${surface.border}`,
    ...(width ? { width } : { flex: 1 }),
    ...(color ? { color, fontWeight: 600 } : { color: "#334155" }),
  }),
  summaryPanel: {
    padding: "20px 22px 22px",
    background: surface.muted,
    borderTop: `1px solid ${surface.border}`,
  },
  summaryTitle: {
    margin: "0 0 14px",
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "-0.01em",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: 12,
  },
  summaryItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    backgroundColor: "#fff",
    padding: "14px 16px",
    borderRadius: 10,
    border: `1px solid ${surface.border}`,
    fontSize: 13,
  },
  summaryLabel: { fontWeight: 600, color: "#64748b" },
  summaryValue: { color: "#312e81", fontWeight: 700, fontVariantNumeric: "tabular-nums" },
  loadingWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 220,
    gap: 18,
    padding: 40,
    marginBottom: 24,
    backgroundColor: surface.card,
    borderRadius: 16,
    border: `1px solid ${surface.border}`,
    boxShadow: "0 2px 4px rgba(15, 23, 42, 0.04)",
  },
  loadingText: {
    color: "#64748b",
    fontSize: 15,
    fontWeight: 500,
  },
  emptyHint: {
    textAlign: "center",
    padding: "48px 24px",
    color: "#94a3b8",
    fontSize: 15,
    backgroundColor: "rgba(255,255,255,0.5)",
    borderRadius: 12,
    border: `1px dashed ${surface.borderStrong}`,
    marginTop: 8,
  },
};

/** Normalize rows from GET /employee-data/all (shape varies by API version). */
function parseEmployeeDataAllRecords(response) {
  const body = response?.data;
  if (!body) return [];
  const nested = body.data;
  if (Array.isArray(nested)) return nested;
  if (nested && typeof nested === "object") {
    if (Array.isArray(nested.records)) return nested.records;
    if (Array.isArray(nested.items)) return nested.items;
    if (Array.isArray(nested.data)) return nested.data;
  }
  if (Array.isArray(body.records)) return body.records;
  if (Array.isArray(body)) return body;
  return [];
}

function collectEmployeeIdsFromDataRows(rows) {
  const ids = new Set();
  const add = (v) => {
    if (v == null) return;
    const s = String(v).trim();
    if (s) ids.add(s);
  };
  const visit = (row) => {
    if (!row || typeof row !== "object") return;
    add(row.employeeId ?? row.EmployeeId);
    const entries = row.entries;
    if (Array.isArray(entries)) {
      entries.forEach((entry) => {
        if (entry && typeof entry === "object") {
          add(entry.employeeId ?? entry.EmployeeId);
        }
      });
    }
  };
  if (Array.isArray(rows)) rows.forEach(visit);
  return ids;
}

/** Roster from GET /employee-ids (same contract as EmployeeTicketsList). */
function parseEmployeeIdsList(response) {
  const list = response?.data?.data ?? response?.data;
  if (!Array.isArray(list)) return [];
  const ids = new Set();
  list.forEach((item) => {
    if (typeof item === "string") {
      const s = item.trim();
      if (s) ids.add(s);
      return;
    }
    if (item && typeof item === "object") {
      const id = item.employeeId ?? item.EmployeeId;
      if (id != null) {
        const s = String(id).trim();
        if (s) ids.add(s);
      }
    }
  });
  return ids;
}

const ExcelStyleAttendance = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    startDate: format(new Date(), "yyyy-MM-dd"),
    endDate: format(new Date(), "yyyy-MM-dd"),
  });
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [downloadHover, setDownloadHover] = useState(false);
  const [employeesList, setEmployeesList] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [employeesFetchError, setEmployeesFetchError] = useState("");

  const allSelected =
    employeesList.length > 0 && selectedEmployeeIds.length === employeesList.length;

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setEmployeesLoading(true);
        setEmployeesFetchError("");
        const ids = new Set();

        const [rosterRes, dataAllRes] = await Promise.allSettled([
          api.get("/employee-ids"),
          api.get("/employee-data/all", { params: { limit: 10000 } }),
        ]);

        if (rosterRes.status === "fulfilled") {
          parseEmployeeIdsList(rosterRes.value).forEach((id) => ids.add(id));
        } else {
          console.warn("employee-ids failed:", rosterRes.reason);
        }

        if (dataAllRes.status === "fulfilled") {
          const rows = parseEmployeeDataAllRecords(dataAllRes.value);
          collectEmployeeIdsFromDataRows(rows).forEach((id) => ids.add(id));
        } else {
          console.warn("employee-data/all failed:", dataAllRes.reason);
        }

        if (ids.size === 0) {
          setEmployeesFetchError("Could not load employee list.");
          setEmployeesList([]);
        } else {
          setEmployeesList([...ids].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })));
        }
      } catch (err) {
        console.error(err);
        setEmployeesFetchError("Could not load employee list.");
        setEmployeesList([]);
      } finally {
        setEmployeesLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    const defaultEndDate = new Date(formData.startDate);
    defaultEndDate.setDate(defaultEndDate.getDate() + 30);
    setFormData((prev) => ({
      ...prev,
      endDate: format(defaultEndDate, "yyyy-MM-dd"),
    }));
  }, [formData.startDate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleEmployeeId = (id) => {
    setSelectedEmployeeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAllEmployees = () => {
    setSelectedEmployeeIds((prev) =>
      prev.length === employeesList.length ? [] : [...employeesList]
    );
  };

  const handleClearEmployeeSelection = () => {
    setSelectedEmployeeIds([]);
  };

  const validateDates = () => {
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);

    if (start > end) {
      setError("Start date cannot be after end date");
      return false;
    }

    if (selectedEmployeeIds.length === 0) {
      setError("Select at least one employee (use checkboxes).");
      return false;
    }

    setError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateDates()) return;

    setLoading(true);
    try {
      const orderedIds = employeesList.filter((id) => selectedEmployeeIds.includes(id));
      const extras = selectedEmployeeIds.filter((id) => !employeesList.includes(id));
      const employeeIdParam = [...orderedIds, ...extras].join(",");

      const response = await api.get("/attendance-analysis", {
        params: {
          startDate: formData.startDate,
          endDate: formData.endDate,
          employeeId: employeeIdParam,
        },
      });

      const rawData = response.data?.data ?? response.data;
      const isMultiple = Array.isArray(rawData);

      let transformedData;
      if (isMultiple) {
        transformedData = rawData.map((empData) => transformToExcelFormat(empData));
      } else if (rawData && typeof rawData === "object") {
        transformedData = [transformToExcelFormat(rawData)];
      } else {
        transformedData = [];
      }
      setAttendanceData(Array.isArray(transformedData) ? transformedData : []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch attendance data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const transformToExcelFormat = (apiData) => {
    if (!apiData || !apiData.summary) {
      return { employeeId: apiData?.employeeId || "Unknown", data: [], summary: { totalDays: 0, presentDays: 0, pendingDays: 0, holidays: 0 } };
    }
    const start = new Date(apiData.startDate);
    const end = new Date(apiData.endDate);
    const allDates = eachDayOfInterval({ start, end });

    const datesWithData = new Set();
    (apiData.summary.daysDataReceived || []).forEach((day) => {
      datesWithData.add(day.date);
    });

    const isSecondOrFourthSaturday = (date) => {
      if (date.getDay() !== 6) return false;
      const dateOfMonth = date.getDate();
      const weekOfMonth = Math.ceil(dateOfMonth / 7);
      return weekOfMonth === 2 || weekOfMonth === 4;
    };

    const transformedData = allDates.map((date) => {
      const dateStr = format(date, "yyyy-MM-dd");
      const dayName = format(date, "EEEE");
      const isWeekendDay = isWeekend(date);
      const isHoliday = isSunday(date);
      const isSecondOrFourthSat = isSecondOrFourthSaturday(date);

      let status;
      if (isHoliday) {
        status = "Holiday";
      } else if (isSecondOrFourthSat) {
        status = "Holiday";
      } else if (datesWithData.has(dateStr)) {
        status = "Present";
      } else {
        status = "Pending";
      }

      return {
        date: dateStr,
        day: dayName,
        status: status,
        isWeekend: isWeekendDay,
        isHoliday: isHoliday || isSecondOrFourthSat,
        isSecondOrFourthSaturday: isSecondOrFourthSat,
        hours: datesWithData.has(dateStr) ? "Submitted" : "Not Submitted",
      };
    });

    const pendingDaysCount = transformedData.filter(day =>
      day.status === "Pending" &&
      !day.isHoliday &&
      !day.isSecondOrFourthSaturday
    ).length;

    return {
      employeeId: apiData.employeeId,
      data: transformedData,
      summary: {
        totalDays: allDates.length,
        presentDays: apiData.summary.daysDataReceivedCount ?? 0,
        pendingDays: pendingDaysCount,
        holidays: allDates.filter((date) => isSunday(date) || isSecondOrFourthSaturday(date)).length,
      },
    };
  };

  const downloadAsWord = () => {
    const dataToUse = Array.isArray(attendanceData) ? attendanceData : [];
    if (dataToUse.length === 0) {
      alert("No data to download. Please generate a report first.");
      return;
    }

    let htmlContent = `
      <html>
        <head>
          <meta charset="utf-8">
          <title>Employee Log Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; text-align: center; }
            .header-info { margin: 20px 0; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .present { color: #1d6f42; }
            .holiday { color: #1a5e9e; }
            .pending { color: #c33; }
            .weekend { background-color: #f9f9f9; }
            .summary { margin-top: 30px; }
            .summary h3 { color: #333; }
            .summary-table { width: 50%; }
            .employee-section { margin-bottom: 40px; }
          </style>
        </head>
        <body>
          <h1>Employee Log Report</h1>
          <div class="header-info">
            <p><strong>Report Period:</strong> ${format(parseISO(formData.startDate), "MMM dd, yyyy")} to ${format(parseISO(formData.endDate), "MMM dd, yyyy")}</p>
            <p><strong>Generated on:</strong> ${format(new Date(), "MMM dd, yyyy")}</p>
          </div>
    `;

    dataToUse.forEach((empData) => {
      htmlContent += `
        <div class="employee-section">
          <h2>Employee ID: ${empData.employeeId}</h2>
          <div class="summary">
            <h3>Log for ${empData.employeeId}</h3>
            <table class="summary-table">
              <tr><td><strong>Total Days:</strong></td><td>${empData.summary.totalDays}</td></tr>
              <tr><td><strong>Enter log Days:</strong></td><td>${empData.summary.presentDays}</td></tr>
              <tr><td><strong>Pending log Day: with Employee leave</strong></td><td>${empData.summary.pendingDays}</td></tr>
              <tr><td><strong>Holidays (Sundays & 2nd/4th Saturdays):</strong></td><td>${empData.summary.holidays}</td></tr>
            </table>
          </div>
        </div>
      `;
    });

    htmlContent += `</body></html>`;

    const blob = new Blob([htmlContent], { type: "application/msword" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Dailylog_Report.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const reportPeriodLabel = `${format(parseISO(formData.startDate), "MMM d, yyyy")} → ${format(parseISO(formData.endDate), "MMM d, yyyy")}`;

  const renderEmployeeData = (empData) => (
    <div key={empData.employeeId} style={styles.employeeSection}>
      <div style={styles.resultCard}>
        <div style={styles.employeeTitleBar}>
          <h2 style={styles.employeeTitle}>Employee {empData.employeeId}</h2>
          <span style={styles.employeeTitleMeta}>{reportPeriodLabel}</span>
        </div>
        <div style={styles.tableContainer}>
          <div style={styles.headerRow}>
            <div style={styles.headerCell("120px")}>Date</div>
            <div style={styles.headerCell("100px")}>Day</div>
            <div style={styles.headerCell("100px")}>Status</div>
            <div style={styles.headerCell()}>Notes</div>
          </div>

          {empData.data.map((row, index) => {
            const statusColor = row.status === "Present" ? "#15803d" : row.status === "Holiday" ? "#1d4ed8" : "#dc2626";
            return (
              <div
                key={index}
                style={styles.dataRow(row.isWeekend, index % 2 === 0)}
              >
                <div style={styles.dataCell("120px")}>{format(parseISO(row.date), "MMM dd, yyyy")}</div>
                <div style={styles.dataCell("100px")}>{row.day}</div>
                <div style={styles.dataCell("100px", statusColor)}>{row.status}</div>
                <div style={styles.dataCell()}>
                  {row.status === "Present"
                    ? "Data submitted"
                    : row.status === "Holiday"
                      ? row.isSecondOrFourthSaturday ? "2nd/4th Saturday holiday" : "Sunday holiday"
                      : "No data available"}
                </div>
              </div>
            );
          })}
        </div>

        <div style={styles.summaryPanel}>
          <h3 style={styles.summaryTitle}>Summary</h3>
          <div style={styles.summaryGrid}>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Total days</span>
              <span style={styles.summaryValue}>{empData.summary.totalDays}</span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Log entered</span>
              <span style={styles.summaryValue}>{empData.summary.presentDays}</span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Pending</span>
              <span style={styles.summaryValue}>{empData.summary.pendingDays}</span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Holidays</span>
              <span style={styles.summaryValue}>{empData.summary.holidays}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const hasData = Array.isArray(attendanceData) && attendanceData.length > 0;

  return (
    <div style={styles.container}>
      <div style={styles.shell}>
        <div style={styles.topBar}>
          <button
            type="button"
            aria-label="Go back"
            onClick={() => navigate(-1)}
            style={styles.backBtn}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#fff";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(15, 23, 42, 0.08)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.85)";
              e.currentTarget.style.boxShadow = "0 1px 2px rgba(15, 23, 42, 0.06)";
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 22 }} />
          </button>
          {hasData && (
            <button
              type="button"
              onClick={downloadAsWord}
              style={styles.downloadBtn(downloadHover)}
              onMouseEnter={() => setDownloadHover(true)}
              onMouseLeave={() => setDownloadHover(false)}
            >
              <DescriptionOutlinedIcon sx={{ fontSize: 20 }} />
              Export Word
            </button>
          )}
        </div>

        <header style={styles.hero}>
          <h1 style={styles.pageTitle}>
            <span style={styles.pageTitleGradient}>Employee log analysis</span>
          </h1>
          <p style={styles.pageSubtitle}>
            Tick one or more employees (or use Select all), set the date range, then generate a daily log report with holidays and pending days highlighted.
          </p>
        </header>

        <form onSubmit={handleSubmit} style={styles.filterCard}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>Report parameters</h2>
            <p style={styles.cardHint}>
              End date defaults to 30 days after the start date. Select multiple employees with checkboxes; the report request sends their IDs as a comma-separated list.
            </p>
          </div>

          <div style={styles.employeePickerBlock}>
            <div style={styles.employeeToolbar}>
              <div style={styles.employeeToolbarLeft}>
                <span style={styles.label}>Employees</span>
                <span style={styles.selectedCount}>
                  {selectedEmployeeIds.length} selected
                </span>
              </div>
              <div style={styles.employeeToolbarActions}>
                <button
                  type="button"
                  style={styles.textBtn}
                  onClick={handleSelectAllEmployees}
                  disabled={employeesLoading || employeesList.length === 0}
                >
                  {allSelected ? "Deselect all" : "Select all"}
                </button>
                <button
                  type="button"
                  style={{ ...styles.textBtn, ...styles.textBtnMuted }}
                  onClick={handleClearEmployeeSelection}
                  disabled={employeesLoading || selectedEmployeeIds.length === 0}
                >
                  Clear
                </button>
              </div>
            </div>
            <div style={styles.checkboxGrid}>
              {employeesLoading && (
                <span style={{ gridColumn: "1 / -1", fontSize: 14, color: "#64748b", padding: 8 }}>
                  Loading roster…
                </span>
              )}
              {!employeesLoading &&
                employeesList.map((id) => {
                  const checked = selectedEmployeeIds.includes(id);
                  return (
                    <label
                      key={id}
                      style={{
                        ...styles.checkboxLabel,
                        ...(checked ? { backgroundColor: "rgba(79, 70, 229, 0.06)", borderColor: "rgba(79, 70, 229, 0.25)" } : {}),
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleEmployeeId(id)}
                        style={styles.checkboxInput}
                      />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={id}>
                        {id}
                      </span>
                    </label>
                  );
                })}
              {!employeesLoading && employeesList.length === 0 && !employeesFetchError && (
                <span style={{ gridColumn: "1 / -1", fontSize: 14, color: "#64748b", padding: 8 }}>
                  No employees in roster.
                </span>
              )}
            </div>
            {employeesFetchError && (
              <span
                style={{
                  fontSize: 12,
                  color: "#b91c1c",
                  marginTop: 8,
                  display: "block",
                }}
              >
                {employeesFetchError}
              </span>
            )}
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="employee-log-start">
                Start date
              </label>
              <input
                id="employee-log-start"
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
                style={styles.input}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="employee-log-end">
                End date
              </label>
              <input
                id="employee-log-end"
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                required
                style={styles.input}
              />
            </div>
            <div style={styles.formActions}>
              <button type="submit" disabled={loading} style={styles.submitBtn(loading)}>
                {loading ? "Generating…" : "Generate report"}
              </button>
            </div>
          </div>
          {error && <div style={styles.errorMessage}>{error}</div>}
        </form>

        {loading && (
          <div style={styles.loadingWrap}>
            <CircularProgress size={44} sx={{ color: "#4f46e5" }} />
            <span style={styles.loadingText}>Fetching attendance for the selected range…</span>
          </div>
        )}

        {!loading && hasData && (Array.isArray(attendanceData) ? attendanceData : []).map((empData) => renderEmployeeData(empData))}

        {!loading && !hasData && (
          <div style={styles.emptyHint}>
            Results will appear here after you generate a report. Select one or more employees and dates above to begin.
          </div>
        )}
      </div>
    </div>
  );
};

export default ExcelStyleAttendance;
